import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const fetchSystemSummary = async () => {
  const response = await axios.get(`${API}/system-summary`);
  return response.data;
};

export const fetchAtlasNodes = async () => {
  const response = await axios.get(`${API}/atlas`);
  return response.data;
};

export const fetchLibrary = async (params = {}) => {
  const response = await axios.get(`${API}/library`, { params });
  return response.data;
};

export const fetchLibraryItem = async (slug) => {
  const response = await axios.get(`${API}/library/${slug}`);
  return response.data;
};

export const fetchGlossary = async (params = {}) => {
  const response = await axios.get(`${API}/glossary`, { params });
  return response.data;
};

export const fetchNotes = async (itemSlug) => {
  const response = await axios.get(`${API}/notes`, { params: { item_slug: itemSlug } });
  return response.data;
};

export const createNote = async (payload) => {
  const response = await axios.post(`${API}/notes`, payload);
  return response.data;
};

export const updateNote = async (noteId, payload) => {
  const response = await axios.put(`${API}/notes/${noteId}`, payload);
  return response.data;
};

export const deleteNote = async (noteId) => {
  const response = await axios.delete(`${API}/notes/${noteId}`);
  return response.data;
};

export const createVipCheckoutSession = async (payload) => {
  const response = await axios.post(`${API}/payments/vip/checkout`, payload);
  return response.data;
};

export const fetchVipCheckoutStatus = async (sessionId) => {
  const response = await axios.get(`${API}/payments/vip/status/${sessionId}`);
  return response.data;
};

export const checkVipAccess = async (payload) => {
  const response = await axios.post(`${API}/vip/access/check`, payload);
  return response.data;
};

export const sendVipChatMessage = async (payload) => {
  const response = await axios.post(`${API}/vip/chat/message`, payload);
  return response.data;
};

export const fetchVipChatHistory = async (email, sessionId) => {
  const response = await axios.get(`${API}/vip/chat/history`, {
    params: {
      email,
      session_id: sessionId || undefined,
    },
  });
  return response.data;
};

export const fetchVipMemberAccount = async (email) => {
  const response = await axios.get(`${API}/vip/member/account`, { params: { email } });
  return response.data;
};

export const fetchVipPurchaseHistory = async (email) => {
  const response = await axios.get(`${API}/vip/member/purchases`, { params: { email } });
  return response.data;
};

export const fetchVipChatSessions = async (email) => {
  const response = await axios.get(`${API}/vip/chat/sessions`, { params: { email } });
  return response.data;
};

export const updateVipChatSession = async (sessionId, email, payload) => {
  const response = await axios.patch(`${API}/vip/chat/sessions/${sessionId}`, payload, { params: { email } });
  return response.data;
};

export const deleteVipChatSession = async (sessionId, email) => {
  const response = await axios.delete(`${API}/vip/chat/sessions/${sessionId}`, { params: { email } });
  return response.data;
};

export const speakKonshusVoice = async (payload) => {
  const response = await axios.post(`${API}/vip/voice/speak`, payload);
  return response.data;
};

// ========== PERSONALIZATION API ==========
export const fetchGreetingPresets = async () => {
  const response = await axios.get(`${API}/vip/konshus/greeting-presets`);
  return response.data;
};

export const fetchKonshusPreferences = async (email) => {
  const response = await axios.get(`${API}/vip/konshus/preferences`, { params: { email } });
  return response.data;
};

export const updateKonshusPreferences = async (payload) => {
  const response = await axios.put(`${API}/vip/konshus/preferences`, payload);
  return response.data;
};

// ========== COMMUNITY API ==========
export const fetchCommunityCategories = async () => {
  const response = await axios.get(`${API}/vip/community/categories`);
  return response.data;
};

export const fetchCommunityThreads = async (email, categoryId) => {
  const response = await axios.get(`${API}/vip/community/threads`, {
    params: { email, category_id: categoryId || undefined },
  });
  return response.data;
};

export const createCommunityThread = async (payload) => {
  const response = await axios.post(`${API}/vip/community/threads`, payload);
  return response.data;
};

export const fetchCommunityThread = async (threadId, email) => {
  const response = await axios.get(`${API}/vip/community/threads/${threadId}`, { params: { email } });
  return response.data;
};

export const fetchThreadReplies = async (threadId, email) => {
  const response = await axios.get(`${API}/vip/community/threads/${threadId}/replies`, { params: { email } });
  return response.data;
};

export const createThreadReply = async (threadId, payload) => {
  const response = await axios.post(`${API}/vip/community/threads/${threadId}/replies`, payload);
  return response.data;
};

export const deleteCommunityThread = async (threadId, email) => {
  const response = await axios.delete(`${API}/vip/community/threads/${threadId}`, { params: { email } });
  return response.data;
};

// ========== ACHIEVEMENT API ==========
export const fetchAchievementDefinitions = async () => {
  const response = await axios.get(`${API}/vip/achievements/definitions`);
  return response.data;
};

export const fetchAchievementProgress = async (email) => {
  const response = await axios.get(`${API}/vip/achievements/progress`, { params: { email } });
  return response.data;
};

export const checkAchievements = async (email) => {
  const response = await axios.post(`${API}/vip/achievements/check`, null, { params: { email } });
  return response.data;
};

// ========== EXPLORATION TRACKING API ==========
export const trackExploration = async (payload) => {
  const response = await axios.post(`${API}/vip/achievements/track-exploration`, payload);
  return response.data;
};

// ========== NOTIFICATION API ==========
export const fetchNotifications = async (email, limit = 50) => {
  const response = await axios.get(`${API}/vip/notifications`, { params: { email, limit } });
  return response.data;
};

export const fetchNotificationCount = async (email) => {
  const response = await axios.get(`${API}/vip/notifications/count`, { params: { email } });
  return response.data;
};

export const markNotificationsRead = async (email, notificationIds) => {
  const response = await axios.post(`${API}/vip/notifications/mark-read`, {
    email,
    notification_ids: notificationIds,
  });
  return response.data;
};

export const markAllNotificationsRead = async (email) => {
  const response = await axios.post(`${API}/vip/notifications/mark-all-read`, null, { params: { email } });
  return response.data;
};

export const deleteNotification = async (notificationId, email) => {
  const response = await axios.delete(`${API}/vip/notifications/${notificationId}`, { params: { email } });
  return response.data;
};

// ========== COMMUNITY THREAD PINNING API ==========
export const toggleThreadPin = async (threadId, email, pinned) => {
  const response = await axios.patch(`${API}/vip/community/threads/${threadId}/pin`, {
    email,
    pinned,
  });
  return response.data;
};

// ========== DIGEST EMAIL API ==========
export const fetchDigestPreferences = async (email) => {
  const response = await axios.get(`${API}/vip/digest/preferences`, { params: { email } });
  return response.data;
};

export const updateDigestPreferences = async (payload) => {
  const response = await axios.put(`${API}/vip/digest/preferences`, payload);
  return response.data;
};

export const sendTestDigest = async (email) => {
  const response = await axios.post(`${API}/vip/digest/send-test`, { email });
  return response.data;
};

// ========== LEADERBOARD API ==========
export const fetchLeaderboard = async (email, limit = 10) => {
  const response = await axios.get(`${API}/vip/leaderboard`, { params: { email, limit } });
  return response.data;
};

// ========== ADMIN API ==========
export const checkAdminStatus = async (email) => {
  const response = await axios.get(`${API}/vip/admin/check`, { params: { email } });
  return response.data;
};

export const adminModerateThread = async (threadId, payload) => {
  const response = await axios.patch(`${API}/vip/admin/threads/${threadId}`, payload);
  return response.data;
};

export const adminDeleteThread = async (threadId, email) => {
  const response = await axios.delete(`${API}/vip/admin/threads/${threadId}`, { params: { email } });
  return response.data;
};

// ========== INITIATION API ==========
export const fetchInitiationSteps = async () => {
  const response = await axios.get(`${API}/vip/initiation/steps`);
  return response.data;
};

export const fetchInitiationProgress = async (email) => {
  const response = await axios.get(`${API}/vip/initiation/progress`, { params: { email } });
  return response.data;
};

export const completeInitiationStep = async (payload) => {
  const response = await axios.post(`${API}/vip/initiation/complete-step`, payload);
  return response.data;
};

// ========== CORRESPONDENCES API ==========
export const fetchCorrespondencesMap = async (email) => {
  const response = await axios.get(`${API}/vip/correspondences/map`, { params: { email } });
  return response.data;
};